/**
 * IndexedDB persistence for the offline write queue.
 *
 * Stores:
 *   - writeQueue: pending ops, keyed by local id, with a `createdAt` index so
 *     we replay in FIFO order (a checkout must replay after its check-in).
 *   - idMap: local check-in id ("local:uuid") → real check_ins.id, written when
 *     a queued check-in syncs. Dependent ops (break/checkout/submit) resolve
 *     their target row through this so they survive reloads between syncs.
 *   - activeCheckIn: the staffer's current active-check-in row, keyed by
 *     staffId, so the live shift timer survives a reload while offline (the
 *     React Query cache is in-memory and would be lost).
 */
import { openDB, type IDBPDatabase } from "idb";
import type { QueueItem } from "./types";

const DB_NAME = "studiopark-offline";
const DB_VERSION = 3;
const STORE = "writeQueue";
const ID_MAP = "idMap";
const ACTIVE = "activeCheckIn";

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex("createdAt", "createdAt");
        }
        if (!db.objectStoreNames.contains(ID_MAP)) {
          db.createObjectStore(ID_MAP);
        }
        if (!db.objectStoreNames.contains(ACTIVE)) {
          db.createObjectStore(ACTIVE);
        }
      },
    });
  }
  return dbPromise;
}

export async function putItem(item: QueueItem): Promise<void> {
  const db = await getDB();
  await db.put(STORE, item);
}

export async function deleteItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE, id);
}

/** All queued items, oldest first (FIFO replay order). */
export async function getAllItems(): Promise<QueueItem[]> {
  const db = await getDB();
  return (await db.getAllFromIndex(STORE, "createdAt")) as QueueItem[];
}

/** Record the server id a synced check-in produced, for dependent ops. */
export async function putIdMap(localId: string, realId: string): Promise<void> {
  const db = await getDB();
  await db.put(ID_MAP, realId, localId);
}

export async function getIdMap(localId: string): Promise<string | undefined> {
  const db = await getDB();
  return (await db.get(ID_MAP, localId)) as string | undefined;
}

/** Persisted active check-in, keyed by staffId. Stored as an opaque value
 *  (the ActiveCheckIn shape lives in the checkin feature); callers cast. */
export async function getActiveLocal(staffId: string): Promise<unknown | null> {
  const db = await getDB();
  return (await db.get(ACTIVE, staffId)) ?? null;
}

export async function setActiveLocal(staffId: string, value: unknown | null): Promise<void> {
  const db = await getDB();
  if (value === null || value === undefined) await db.delete(ACTIVE, staffId);
  else await db.put(ACTIVE, value, staffId);
}
